import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BsCameraVideoFill, BsClockHistory } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { consultationApi } from '../../api/consultationApi';
import { useRealtime } from '../../context/RealtimeContext';
import '../../styles/activeCall.css';

const JOINABLE_STATUSES = ['confirmed', 'in_progress'];

function formatCountdown(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

const ActiveCallButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useRealtime();
  const [joinable, setJoinable] = useState([]);
  const [starting, setStarting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);

  const onCallPage = location.pathname.startsWith('/video-consultation');
  const loggedIn = Boolean(localStorage.getItem('token'));

  const load = useCallback(async () => {
    if (!loggedIn) {
      setJoinable([]);
      return;
    }
    try {
      const requests = [consultationApi.getMyBookings('video').catch(() => ({ data: {} }))];
      // Advocate endpoint 403s for non-advocates — safe to attempt & ignore.
      requests.push(consultationApi.getAdvocateBookings('video').catch(() => ({ data: {} })));
      const results = await Promise.all(requests);

      const map = new Map();
      results.forEach(({ data }) => {
        (data.bookings || []).forEach((b) => {
          if (b.joinableNow && JOINABLE_STATUSES.includes(b.status)) {
            map.set(b._id, b);
          }
        });
      });
      setJoinable([...map.values()]);
    } catch (_) {
      /* ignore */
    }
  }, [loggedIn]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, [load, location.pathname]);

  // Ticking clock so the countdown updates and the button flips on exactly at
  // the scheduled start time.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const startCall = useCallback(async (booking) => {
    setStarting(true);
    try {
      await consultationApi.startCall(booking._id);
      navigate(`/video-consultation/${booking._id}?call=start`);
    } catch (err) {
      const msg = err.response?.data?.msg || 'Unable to start the video call. Please try again.';
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  }, [navigate]);

  if (onCallPage || !loggedIn || joinable.length === 0) return null;

  // Prefer a booking whose exact start time has arrived; otherwise the soonest.
  const sorted = [...joinable].sort((a, b) => {
    const aReached = now >= new Date(a.scheduledStart).getTime();
    const bReached = now >= new Date(b.scheduledStart).getTime();
    if (aReached !== bReached) return aReached ? -1 : 1;
    return new Date(a.scheduledStart) - new Date(b.scheduledStart);
  });
  const booking = sorted[0];

  const startMs = new Date(booking.scheduledStart).getTime();
  const reached = now >= startMs;
  const msLeft = startMs - now;

  const isAdvocate = currentUser && String(booking.advocateId?._id || booking.advocateId) === String(currentUser._id);
  const other = isAdvocate ? booking.clientId : booking.advocateId;
  const otherName = other
    ? `${isAdvocate ? '' : 'Adv. '}${other.firstName || ''} ${other.lastName || ''}`.trim()
    : 'your consultation';

  return (
    <div className={`active-call-banner ${reached ? '' : 'upcoming'}`} role="alert">
      <div className="active-call-info">
        <span className={`active-call-dot ${reached ? '' : 'idle'}`} />
        <div>
          <div className="active-call-title">
            {reached ? 'Video consultation ready' : 'Video consultation starting soon'}
          </div>
          <div className="active-call-sub notranslate" translate="no">
            {reached ? `with ${otherName}` : `with ${otherName} · starts in ${formatCountdown(msLeft)}`}
          </div>
        </div>
      </div>
      {reached ? (
        <button
          type="button"
          className="active-call-btn"
          onClick={() => startCall(booking)}
          disabled={starting}
        >
          <BsCameraVideoFill />
          <span>{starting ? 'Connecting…' : 'Video Call'}</span>
        </button>
      ) : (
        <button type="button" className="active-call-btn waiting" disabled title="The call activates at the scheduled time">
          <BsClockHistory />
          <span>{formatCountdown(msLeft)}</span>
        </button>
      )}
    </div>
  );
};

export default ActiveCallButton;
